import * as React from 'react'

import {
  IViewOptionsColumnItem,
  IViewOptionsPanelProps,
  IViewOptionsSortValue,
} from './types'

interface IViewOptionsDraftState {
  grouping: string | null
  sort: IViewOptionsSortValue
  columns?: IViewOptionsColumnItem[]
  columnsReset?: boolean
}

interface IViewOptionsDraft {
  panelProps: IViewOptionsPanelProps
  commit: () => void
}

// Stages ViewOptions edits in a local draft seeded once from the live sections.
// The returned panel props read and write the draft, so nothing reaches the
// domain until commit() replays the draft into the real onChange handlers. The
// draft is discarded simply by unmounting (Cancel / sheet close), which reseeds
// on the next open. Used only by the mobile sheet; the desktop popover renders
// the panel live and never touches this hook.
export default function useViewOptionsDraft(
  props: IViewOptionsPanelProps,
): IViewOptionsDraft {
  const { grouping, sorting, columns, autoFocusSearch } = props

  const [draft, setDraft] = React.useState<IViewOptionsDraftState>(() => ({
    grouping: grouping?.value ?? null,
    sort: sorting?.value ?? { key: null },
    columns: columns?.items,
  }))

  const panelProps = React.useMemo<IViewOptionsPanelProps>(
    () => ({
      autoFocusSearch,
      grouping: grouping
        ? {
            ...grouping,
            value: draft.grouping,
            onChange: key => setDraft(prev => ({ ...prev, grouping: key })),
          }
        : undefined,
      sorting: sorting
        ? {
            ...sorting,
            value: draft.sort,
            onChange: value => setDraft(prev => ({ ...prev, sort: value })),
          }
        : undefined,
      columns: columns
        ? {
            ...columns,
            items: draft.columns ?? columns.items,
            onChange: items =>
              setDraft(prev => ({
                ...prev,
                columns: items,
                columnsReset: false,
              })),
            // Reset is staged like any other edit: the draft previews the default
            // layout, and commit() runs the real reset command (which also clears
            // persisted config) only if no column edits followed it.
            onReset: () =>
              setDraft(prev => ({
                ...prev,
                columns: columns.defaultItems,
                columnsReset: true,
              })),
          }
        : undefined,
    }),
    [autoFocusSearch, grouping, sorting, columns, draft],
  )

  const commit = React.useCallback(() => {
    grouping?.onChange(draft.grouping)
    sorting?.onChange(draft.sort)
    if (columns) {
      if (draft.columnsReset) {
        columns.onReset()
      } else {
        columns.onChange(draft.columns ?? columns.items)
      }
    }
  }, [grouping, sorting, columns, draft])

  return { panelProps, commit }
}
